/**
 * 质量档与自适应降档（移动端核心）。
 * - 设备能力检测（移动端）
 * - DPR 上限、分辨率缩放、采样步数分级
 * - 按帧率自适应调整分辨率
 */

export type QualityTier = 'high' | 'medium' | 'low'

export interface QualitySettings {
  dprCap: number
  /** 初始分辨率缩放（0-1） */
  resolutionScale: number
  /** Ray Marching 采样步数 */
  sampleSteps: number
}

const TIER_SETTINGS: Record<QualityTier, QualitySettings> = {
  high: { dprCap: 2, resolutionScale: 1, sampleSteps: 64 },
  medium: { dprCap: 1.5, resolutionScale: 0.9, sampleSteps: 32 },
  low: { dprCap: 1, resolutionScale: 0.7, sampleSteps: 16 },
}

export function detectMobile(): boolean {
  if (typeof navigator === 'undefined') return false
  const coarse = window.matchMedia
    ? window.matchMedia('(pointer: coarse)').matches
    : false
  const touch = navigator.maxTouchPoints > 1
  return coarse && touch
}

export class QualityManager {
  isMobile: boolean
  tier: QualityTier = 'medium'
  resolutionScale = 1

  private fpsEma = 60
  private settings: QualitySettings = TIER_SETTINGS.medium

  constructor() {
    this.isMobile = detectMobile()
    this.setTier(this.isMobile ? 'medium' : 'high')
  }

  get sampleSteps(): number {
    return this.settings.sampleSteps
  }

  get dprCap(): number {
    return this.settings.dprCap
  }

  get settingsSnapshot(): QualitySettings {
    return { ...this.settings, resolutionScale: this.resolutionScale }
  }

  setTier(tier: QualityTier): void {
    this.tier = tier
    this.settings = TIER_SETTINGS[tier]
    this.resolutionScale = this.settings.resolutionScale
  }

  /** 每帧调用：按帧率自适应分辨率 */
  update(dt: number): void {
    const fps = dt > 0 ? 1 / dt : 60
    this.fpsEma += (fps - this.fpsEma) * 0.05
    if (this.fpsEma < 28 && this.resolutionScale > 0.45) {
      this.resolutionScale = Math.max(0.45, this.resolutionScale - 0.08)
    } else if (this.fpsEma > 52 && this.resolutionScale < 1) {
      this.resolutionScale = Math.min(1, this.resolutionScale + 0.03)
    }
  }

  get fps(): number {
    return this.fpsEma
  }
}
